import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

const SVG_W = 960;
const SVG_H = 480;

const REPOS = [
  { id: "platform-fw", label: "platform/framework", ref: "v14.2.1", color: "#60a5fa" },
  { id: "vendor-hal", label: "vendor/hal-drivers", ref: "v3.8.0", color: "#818cf8" },
  { id: "apps-ivi", label: "apps/ivi-launcher", ref: "v2.1.4", color: "#22d3ee" },
  { id: "config-build", label: "config/build-targets", ref: "main", color: "#f59e0b" },
];

const CHILD_STATES: { label: string; color: string; icon: string }[] = [
  { label: "passed", color: "#4ade80", icon: "✓" },
  { label: "failed", color: "#f87171", icon: "✗" },
  { label: "manual", color: "#818cf8", icon: "▶" },
  { label: "passed", color: "#4ade80", icon: "✓" },
];

export function MultiRepoSlide() {
  const manifestX = 60;
  const manifestY = 60;
  const manifestW = 240;
  const manifestH = 200;

  const pipelineX = 380;
  const pipelineY = 40;
  const pipelineW = 200;

  const childX = 640;
  const childGap = 55;
  const childW = 300;

  const mrX = 380;
  const mrY = 350;

  return (
    <Box w="100%" maw={1400} px="xl" h="100%">
      <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
        <SlideTitle
          title="Multi-Repository Management"
          subtitle="GitLab partnership — coordinate CI/CD across repositories from a single manifest"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          style={{ flex: 1, minHeight: 0 }}
        >
          <svg
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            width="100%"
            style={{ display: "block", maxHeight: "64vh" }}
          >
            <defs>
              <marker
                id="mr-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto"
              >
                <path
                  d="M1.5 1.5 L8.5 5 L1.5 8.5"
                  fill="none"
                  stroke="context-stroke"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </marker>
            </defs>

            {/* ── Manifest file ── */}
            <motion.g
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <rect
                x={manifestX}
                y={manifestY}
                width={manifestW}
                height={manifestH}
                rx={10}
                fill="rgba(255,255,255,0.03)"
                stroke="rgba(96,165,250,0.3)"
                strokeWidth={1.5}
              />
              <rect
                x={manifestX}
                y={manifestY}
                width={manifestW}
                height={28}
                rx={10}
                fill="rgba(96,165,250,0.08)"
              />
              <rect
                x={manifestX}
                y={manifestY + 18}
                width={manifestW}
                height={10}
                fill="rgba(96,165,250,0.08)"
              />
              <text
                x={manifestX + 12}
                y={manifestY + 19}
                fill="#60a5fa"
                fontSize={10}
                fontWeight={700}
                fontFamily="'Geist Mono', monospace"
              >
                manifest.yml
              </text>

              {REPOS.map((r, i) => {
                const ly = manifestY + 42 + i * 38;
                return (
                  <g key={r.id}>
                    <text
                      x={manifestX + 14}
                      y={ly}
                      fill={r.color}
                      fontSize={9}
                      fontFamily="'Geist Mono', monospace"
                      fontWeight={600}
                    >
                      {r.label}
                    </text>
                    <text
                      x={manifestX + 14}
                      y={ly + 14}
                      fill="rgba(255,255,255,0.4)"
                      fontSize={8.5}
                      fontFamily="'Geist Mono', monospace"
                    >
                      ref: {r.ref}
                    </text>
                  </g>
                );
              })}
            </motion.g>

            {/* ── Arrow: manifest → parent pipeline ── */}
            <motion.path
              d={`M${manifestX + manifestW},${manifestY + manifestH / 2} L${pipelineX - 10},${manifestY + manifestH / 2}`}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth={1.5}
              markerEnd="url(#mr-arrow)"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.7, duration: 0.4 }}
            />

            {/* ── Parent pipeline ── */}
            <motion.g
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.4 }}
            >
              <rect
                x={pipelineX}
                y={pipelineY}
                width={pipelineW}
                height={REPOS.length * childGap + 40}
                rx={10}
                fill="rgba(74,222,128,0.04)"
                stroke="rgba(74,222,128,0.25)"
                strokeWidth={1.5}
              />
              <text
                x={pipelineX + 14}
                y={pipelineY + 20}
                fill="#4ade80"
                fontSize={10}
                fontWeight={700}
                fontFamily="inherit"
                letterSpacing="0.04em"
              >
                PARENT PIPELINE
              </text>

              {REPOS.map((r, i) => {
                const sy = pipelineY + 40 + i * childGap;
                return (
                  <g key={r.id}>
                    <rect
                      x={pipelineX + 12}
                      y={sy}
                      width={pipelineW - 24}
                      height={36}
                      rx={6}
                      fill="rgba(255,255,255,0.03)"
                      stroke="rgba(255,255,255,0.08)"
                      strokeWidth={1}
                    />
                    <circle
                      cx={pipelineX + 26}
                      cy={sy + 18}
                      r={4}
                      fill={r.color}
                    />
                    <text
                      x={pipelineX + 38}
                      y={sy + 21}
                      fill="rgba(255,255,255,0.7)"
                      fontSize={9}
                      fontFamily="'Geist Mono', monospace"
                      fontWeight={500}
                    >
                      {r.label.split("/")[1]}
                    </text>
                  </g>
                );
              })}
            </motion.g>

            {/* ── Arrows to child pipelines + child status ── */}
            {REPOS.map((r, i) => {
              const sy = pipelineY + 40 + i * childGap + 18;
              const cx = childX;
              const cy = sy;
              const state = CHILD_STATES[i]!;

              return (
                <motion.g
                  key={`child-${r.id}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0 + i * 0.12, duration: 0.3 }}
                >
                  <line
                    x1={pipelineX + pipelineW}
                    y1={sy}
                    x2={cx - 10}
                    y2={cy}
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth={1}
                    strokeDasharray="4 3"
                    markerEnd="url(#mr-arrow)"
                  />
                  <rect
                    x={cx}
                    y={cy - 16}
                    width={childW}
                    height={32}
                    rx={6}
                    fill="rgba(255,255,255,0.02)"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={1}
                  />
                  <text
                    x={cx + 10}
                    y={cy + 4}
                    fill={state.color}
                    fontSize={12}
                    fontWeight={700}
                    fontFamily="'Geist Mono', monospace"
                  >
                    {state.icon}
                  </text>
                  <text
                    x={cx + 28}
                    y={cy + 3}
                    fill="rgba(255,255,255,0.6)"
                    fontSize={9}
                    fontFamily="'Geist Mono', monospace"
                  >
                    {r.label.split("/")[1]} › build › test
                  </text>
                  <text
                    x={cx + childW - 10}
                    y={cy + 3}
                    fill={state.color}
                    fontSize={8.5}
                    fontWeight={600}
                    fontFamily="inherit"
                    textAnchor="end"
                  >
                    {state.label}
                  </text>
                </motion.g>
              );
            })}

            {/* ── Merge request automation block ── */}
            <motion.g
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.4 }}
            >
              <rect
                x={mrX}
                y={mrY}
                width={500}
                height={70}
                rx={10}
                fill="rgba(167,139,250,0.05)"
                stroke="rgba(167,139,250,0.25)"
                strokeWidth={1.5}
              />
              <text
                x={mrX + 16}
                y={mrY + 22}
                fill="#a78bfa"
                fontSize={10}
                fontWeight={700}
                fontFamily="inherit"
                letterSpacing="0.04em"
              >
                VIRTUAL MONOREPO
              </text>
              <text
                x={mrX + 16}
                y={mrY + 40}
                fill="rgba(255,255,255,0.6)"
                fontSize={9.5}
                fontFamily="inherit"
              >
                Parent manifest MR manages child MRs across all repositories
              </text>
              <text
                x={mrX + 16}
                y={mrY + 56}
                fill="rgba(255,255,255,0.4)"
                fontSize={9}
                fontFamily="inherit"
              >
                Approvals, failures, and manual steps surface in a single view
              </text>
            </motion.g>

            {/* ── Connecting line from parent pipeline to MR block ── */}
            <motion.path
              d={`M${pipelineX + pipelineW / 2},${pipelineY + REPOS.length * childGap + 40} L${pipelineX + pipelineW / 2},${mrY}`}
              stroke="rgba(167,139,250,0.25)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 1.4, duration: 0.3 }}
            />
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
